/**
 * chart.ts — the uPlot config that MUST be identical in the site's UplotChart
 * and the terminal's ChartPanel (WEB_TERMINAL_UNIFICATION A6). Both charts had
 * their own byte-identical copy of the time-axis label logic and the same
 * axis colours; a drift there means the same candle reads differently in the
 * two products. This is the single source.
 *
 * Deliberately NOT the whole `opts` object: the two charts run different
 * plugin sets (liq-heatmap, sessions, gravity, …) and different Y formatters
 * (site `formatPrice`, terminal `priceDecimals`). Only the parts that are the
 * same in both live here.
 */

/** Resolved theme colours for the axes. They were fixed light-on-dark rgba
 *  strings, so on the Light theme the price scale went near-invisible (found
 *  30.09.2026). uPlot re-evaluates a function `stroke` on every redraw; the
 *  values are cached until <html>'s theme/inline vars change. */
let axisStamp = '';
let axisCache = { x: 'rgba(233,236,239,0.34)', y: 'rgba(233,236,239,0.45)', tick: 'rgba(233,236,239,0.10)', grid: 'rgba(233,236,239,0.06)' };
function axisColors() {
  if (typeof document === 'undefined') return axisCache;
  const root = document.documentElement;
  const stamp = `${root.dataset.theme}|${root.getAttribute('style') ?? ''}`;
  if (stamp === axisStamp) return axisCache;
  axisStamp = stamp;
  const cs = getComputedStyle(root);
  const rgb = (name: string) => {
    const h = cs.getPropertyValue(name).trim().replace('#', '');
    const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
    return Number.isFinite(n) ? `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}` : null;
  };
  const mid = rgb('--mid');
  const hi = rgb('--hi');
  if (mid && hi) axisCache = { x: `rgba(${mid},0.85)`, y: `rgb(${mid})`, tick: `rgba(${hi},0.10)`, grid: `rgba(${hi},0.06)` };
  return axisCache;
}

/** Candle colours for canvas painters (which can't read `var()`): the active
 *  theme's --bull / --bear / --hi as rgb triplets, cached like the axes. The
 *  site's candles were fixed Graphite mint/rose — unreadable on Light. */
let candleStamp = '';
let candleCache = { bull: '61,220,151', bear: '242,96,127', hi: '233,236,239' };
export function candleRgb(): { bull: string; bear: string; hi: string } {
  if (typeof document === 'undefined') return candleCache;
  const root = document.documentElement;
  const stamp = `${root.dataset.theme}|${root.getAttribute('style') ?? ''}`;
  if (stamp === candleStamp) return candleCache;
  candleStamp = stamp;
  const cs = getComputedStyle(root);
  const rgb = (name: string) => {
    const h = cs.getPropertyValue(name).trim().replace('#', '');
    if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(h)) return null;
    const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
    return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
  };
  const bull = rgb('--bull'), bear = rgb('--bear'), hi = rgb('--hi');
  if (bull && bear && hi) candleCache = { bull, bear, hi };
  return candleCache;
}

/** uPlot axis `stroke` / `ticks.stroke` colours — one palette for both charts,
 *  following the active theme. */
export const CHART_AXIS = {
  xStroke: () => axisColors().x,
  yStroke: () => axisColors().y,
  tick: () => axisColors().tick,
  grid: () => axisColors().grid,
} as const;

interface AxisScale { min?: number | null; max?: number | null }
interface AxisPlot {
  scales?: { x?: AxisScale };
  bbox?: { left: number };
  valToPos?(val: number, scale: string, canvasPixels?: boolean): number;
}

/** Gap kept between a canvas label and the plot's left edge, in CSS px. */
export const PLOT_INSET = 4;

/** Left x (canvas px) for a left-anchored label on a uPlot canvas: never closer to the plot edge than PLOT_INSET. */
export function plotInsetX(u: { bbox: { left: number } }, px: number): number {
  return u.bbox.left + PLOT_INSET * px;
}

/** Approximate width of a 11px mono axis label, per character (CSS px). */
const AXIS_CHAR_PX = 6.7;

/**
 * uPlot X-axis `values(u, vals)` — label granularity follows the visible span:
 * intraday shows `HH:MM`, a multi-day view `MM/DD`, past ~6 months `MM/YY`,
 * and Jan 1 becomes the year anchor (`2026`). Timestamps are seconds.
 */
export function axisTimeValues(u: AxisPlot, vals: number[]): string[] {
  const spanDays = ((u.scales?.x?.max ?? 0) - (u.scales?.x?.min ?? 0)) / 86400;
  const leftPad = u.bbox ? u.bbox.left / (globalThis.devicePixelRatio || 1) : 0;
  return vals.map((v) => {
    const s = axisLabel(v);
    // A centred label wider than twice its distance to the canvas edge would be cut off: drop it.
    return u.valToPos && leftPad + u.valToPos(v, 'x') - (s.length * AXIS_CHAR_PX) / 2 < PLOT_INSET ? '' : s;
  });

  function axisLabel(v: number): string {
    const d = new Date(v * 1000);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    if (hh === '00' && mm === '00') {
      if (d.getMonth() === 0 && d.getDate() === 1) return String(d.getFullYear());
      if (spanDays > 180) return `${mo}/${String(d.getFullYear() % 100).padStart(2, '0')}`;
      return `${mo}/${dd}`;
    }
    return `${hh}:${mm}`;
  }
}

// ── Grid + crosshair look (§7.12) — shared by both charts ────────────────────

export type GridMode = 'off' | 'percent' | 'price';
export interface ChartLook {
  gridMode: GridMode;
  gridPercent: number;
  gridPrice: number;
  gridTime: boolean;
  crossStyle: 'dash' | 'solid' | 'dot';
  crossWidth: 1 | 2;
  crossColor: 'mid' | 'hi' | 'acc';
  crossMode: 'both' | 'h' | 'v' | 'off';
  crossLabels: boolean;
  watermark: boolean;
}
export const DEFAULT_CHART_LOOK: ChartLook = {
  gridMode: 'off', gridPercent: 0.5, gridPrice: 100, gridTime: false,
  crossStyle: 'dash', crossWidth: 1, crossColor: 'mid', crossMode: 'both', crossLabels: true, watermark: true,
};
/** "Round numbers" offered for price-step grids. */
export const GRID_PRICE_STEPS = [0.0001, 0.0005, 0.001, 0.005, 0.01, 0.05, 0.1, 0.5, 1, 5, 10, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000, 10000];

const MAX_GRID_LINES = 60;

/** Horizontal grid prices for a visible range [lo, hi]. Lines sit on multiples
 * of the step, so they don't crawl while the range moves. Percent mode turns
 * `gridPercent` of the mid price into a step and snaps it to the nearest
 * round number of GRID_PRICE_STEPS. More than 60 lines → [] (too dense). */
export function gridPrices(lo: number, hi: number, look: Pick<ChartLook, 'gridMode' | 'gridPercent' | 'gridPrice'>): number[] {
  if (look.gridMode === 'off' || !(hi > lo)) return [];
  let step = look.gridPrice;
  if (look.gridMode === 'percent') {
    const raw = ((lo + hi) / 2) * (look.gridPercent / 100);
    step = GRID_PRICE_STEPS.reduce((best, s) => (Math.abs(s - raw) < Math.abs(best - raw) ? s : best), GRID_PRICE_STEPS[0]);
  }
  if (!(step > 0) || (hi - lo) / step > MAX_GRID_LINES) return [];
  const out: number[] = [];
  for (let k = Math.ceil(lo / step); k * step <= hi; k++) out.push(Math.round(k * step * 1e8) / 1e8);
  return out;
}

/** CSS for uPlot's crosshair divs (`.u-cursor-x` vertical, `.u-cursor-y`
 * horizontal). `scope` is a selector prefix so two charts don't share it. */
export function crosshairCss(look: ChartLook, colors: { mid: string; hi: string; acc: string }, scope = ''): string {
  const sty = look.crossStyle === 'solid' ? 'solid' : look.crossStyle === 'dot' ? 'dotted' : 'dashed';
  const line = `${look.crossWidth}px ${sty} ${colors[look.crossColor]}`;
  const showV = look.crossMode === 'both' || look.crossMode === 'v';
  const showH = look.crossMode === 'both' || look.crossMode === 'h';
  return `${scope} .u-cursor-x{border-right:${line} !important;background:none !important;${showV ? '' : 'display:none !important;'}}`
    + `${scope} .u-cursor-y{border-bottom:${line} !important;background:none !important;${showH ? '' : 'display:none !important;'}}`;
}
